import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ContactFormProps, ComplaintFormData } from '../types';
import { RECAPTCHA_CONFIG } from '../constants';
import '../styles/ContactForm.css';
import { useContactForm, useRecaptcha } from '../hooks';
import { submitContactForm } from '../utils';
import LanguagePicker from './LanguagePicker';
import InfoButton from './InfoButton';

const ContactForm: React.FC<ContactFormProps> = ({ 
  onSubmit = submitContactForm,
  className = '' 
}) => {
  const { t } = useTranslation();
  const { formState, updateField, handleSubmit } = useContactForm();
  const { executeRecaptchaAction, isRecaptchaReady } = useRecaptcha({
    siteKey: process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? '',
    action: RECAPTCHA_CONFIG.actions.CONTACT_FORM
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Clear file input when form is successfully submitted
  useEffect(() => {
    if (formState.submitStatusType === 'success' && fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [formState.submitStatusType]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isRecaptchaReady()) {
      return;
    }

    try {
      const token = await executeRecaptchaAction();
      await handleSubmit(onSubmit, token);
    } catch (error) {
      // Error handling is done in the handleSubmit function
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    // Apply 254 character limit to all text fields except problem description (probleem)
    const fieldsWithLimit = ['ettevote', 'epost', 'tellimus'];
    const limitedValue = fieldsWithLimit.includes(name) && value.length > 254 
      ? value.slice(0, 254) 
      : value;
    updateField(name as keyof ComplaintFormData, limitedValue);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files } = e.target;
    updateField(name as keyof ComplaintFormData, files);
  };

  return (
    <div className={`contact-form ${className}`}>
      <div className="contact-form-header">
        <h2 id="form-title">{t('form.title')}</h2>
        <LanguagePicker />
      </div>
      
      <form id="main-form" onSubmit={handleFormSubmit} noValidate aria-labelledby="form-title">
        <div className="form-group">
          <label htmlFor="ettevote">
            {t('form.fields.ettevote.label')} <span aria-label={t('form.fields.ettevote.required')}>*</span>
          </label>
          <input
            type="text"
            id="ettevote"
            name="ettevote"
            value={formState.data.ettevote}
            onChange={handleInputChange}
            required
            maxLength={254}
            placeholder={t('form.fields.ettevote.placeholder')}
            aria-describedby={formState.errors.ettevote ? 'ettevote-error' : undefined}
            aria-invalid={!!formState.errors.ettevote}
          />
          {formState.errors.ettevote && (
            <div id="ettevote-error" className="form-error" role="alert">
              {formState.errors.ettevote}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="epost">
            {t('form.fields.epost.label')} <span aria-label={t('form.fields.epost.required')}>*</span>
          </label>
          <input
            type="email"
            id="epost"
            name="epost"
            value={formState.data.epost}
            onChange={handleInputChange}
            required
            maxLength={254}
            placeholder={t('form.fields.epost.placeholder')}
            aria-describedby={formState.errors.epost ? 'epost-error' : undefined}
            aria-invalid={!!formState.errors.epost}
          />
          {formState.errors.epost && (
            <div id="epost-error" className="form-error" role="alert">
              {formState.errors.epost}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="probleem">
            {t('form.fields.probleem.label')} <span aria-label={t('form.fields.probleem.required')}>*</span>
          </label>
          <textarea
            id="probleem"
            name="probleem"
            value={formState.data.probleem}
            onChange={handleInputChange}
            rows={5}
            required
            placeholder={t('form.fields.probleem.placeholder')}
            aria-describedby={formState.errors.probleem ? 'probleem-error' : undefined}
            aria-invalid={!!formState.errors.probleem}
          />
          {formState.errors.probleem && (
            <div id="probleem-error" className="form-error" role="alert">
              {formState.errors.probleem}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="tellimus" className="label-with-info">
            {t('form.fields.tellimus.label')} <span aria-label={t('form.fields.tellimus.required')}>*</span>
            <InfoButton content={t('form.fields.tellimus.infoText')} id="tellimus-info" />
          </label>
          <input
            type="text"
            id="tellimus"
            name="tellimus"
            value={formState.data.tellimus}
            onChange={handleInputChange}
            required
            maxLength={254}
            placeholder={t('form.fields.tellimus.placeholder')}
            aria-describedby={`tellimus-help ${formState.errors.tellimus ? 'tellimus-error' : ''}`.trim()}
            aria-invalid={!!formState.errors.tellimus}
          />
          <small id="tellimus-help" className="form-help">
            {t('form.fields.tellimus.helpText')}
          </small>
          {formState.errors.tellimus && (
            <div id="tellimus-error" className="form-error" role="alert">
              {formState.errors.tellimus}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="dokument" className="label-with-info">
            {t('form.fields.dokument.label')} <span aria-label={t('form.fields.dokument.required')}>*</span>
            <InfoButton content={t('form.fields.dokument.infoText')} id="dokument-info" />
          </label>
          <div className="file-input-wrapper">
            <input
              ref={fileInputRef}
              type="file"
              id="dokument"
              name="dokument"
              onChange={handleFileChange}
              required
              multiple
              accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
              aria-describedby={`dokument-help ${formState.errors.dokument ? 'dokument-error' : ''}`.trim()}
              aria-invalid={!!formState.errors.dokument}
              className="file-input-hidden"
            />
            <label htmlFor="dokument" className="file-input-button">
              <span className="file-icon">📄</span>
              <span className="file-text">
                {formState.data.dokument && formState.data.dokument.length > 0
                  ? t('form.fields.dokument.filesSelected', { count: formState.data.dokument.length })
                  : t('form.fields.dokument.selectFiles')}
              </span>
            </label>
            {formState.data.dokument && formState.data.dokument.length > 0 && (
              <div className="selected-files" role="status" aria-live="polite">
                <span className="sr-only">
                  {t('accessibility.filesSelected') || `${formState.data.dokument.length} files selected:`}
                </span>
                {Array.from(formState.data.dokument).map((file, index) => (
                  <div key={index} className="selected-file">
                    <span className="file-name">{file.name}</span>
                    <span className="file-size">({Math.round(file.size / 1024)} KB)</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <small id="dokument-help" className="file-help">
            {t('form.fields.dokument.helpText')}
          </small>
          {formState.errors.dokument && (
            <div id="dokument-error" className="form-error" role="alert">
              {formState.errors.dokument}
            </div>
          )}
        </div>

        <button 
          type="submit" 
          disabled={formState.isSubmitting || !isRecaptchaReady()}
          className="submit-button"
          aria-describedby="submit-status"
          aria-live="polite"
          aria-busy={formState.isSubmitting}
        >
          {formState.isSubmitting ? (
            <>
              <span className="loading-spinner" aria-hidden="true"></span>
              <span className="sr-only">{t('accessibility.submitting') || 'Submitting form, please wait...'}</span>
              {t('form.submit.submitting')}
            </>
          ) : (
            t('form.submit.button')
          )}
        </button>
      </form>

      {formState.submitStatus && (
        <div 
          id="submit-status"
          className={`status-message ${formState.submitStatusType === 'error' ? 'error' : 'success'}`}
          role="alert"
          aria-live="polite"
        >
          {formState.submitStatus}
          {formState.submitStatusType === 'error' && (
            <div className="error-recovery-suggestions">
              <strong>{t('errorRecovery.suggestions')}</strong>
              <ul>
                <li>{t('errorRecovery.checkConnection')}</li>
                <li>{t('errorRecovery.checkFields')}</li>
                <li>{t('errorRecovery.checkFiles')}</li>
                <li>{t('errorRecovery.tryLater')}</li>
              </ul>
            </div>
          )}
        </div>
      )}

      {formState.errors.general && (
        <div className="status-message error" role="alert">
          {formState.errors.general}
          <div className="error-recovery-suggestions">
            <strong>{t('errorRecovery.suggestions')}</strong>
            <ul>
              <li>{t('errorRecovery.waitAndRetry')}</li>
              <li>{t('errorRecovery.contactIfPersists')}</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactForm;
